import { useRef, useLayoutEffect, useEffect } from 'react';

/**
 * Renderiza HTML inyectado (dangerouslySetInnerHTML) bloqueando
 * selección, copia y corte del texto mediante 3 capas:
 *  1. CSS user-select:none (clase padre + hijos)
 *  2. inline style !important en cada nodo (anula estilos del CMS)
 *  3. listeners en document para selectstart / selectionchange / copy / cut
 */
const noSelectInlineStyle = {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    WebkitTouchCallout: 'none',
};

function applyNoSelectToNode(el) {
    if (!el || el.nodeType !== Node.ELEMENT_NODE) return;
    el.style.setProperty('user-select', 'none', 'important');
    el.style.setProperty('-webkit-user-select', 'none', 'important');
    el.style.setProperty('-moz-user-select', 'none', 'important');
    el.style.setProperty('-ms-user-select', 'none', 'important');
}

export default function NoSelectHtml({ html, id, className, style }) {
    const rootRef = useRef(null);

    // Capa 2: sobreescribe inline styles del CMS tras cada render
    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        applyNoSelectToNode(root);
        root.querySelectorAll('*').forEach(applyNoSelectToNode);
    }, [html]);

    // Capa 3: listeners en document
    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;

        const selectionTouchesRoot = () => {
            const sel = document.getSelection();
            if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
            for (let i = 0; i < sel.rangeCount; i++) {
                if (sel.getRangeAt(i).intersectsNode(root)) return true;
            }
            return false;
        };

        const onSelectStart = (e) => {
            if (root.contains(e.target)) e.preventDefault();
        };

        const onSelectionChange = () => {
            if (selectionTouchesRoot()) document.getSelection().removeAllRanges();
        };

        const onCopyOrCut = (e) => {
            if (selectionTouchesRoot()) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        document.addEventListener('selectstart', onSelectStart, true);
        document.addEventListener('selectionchange', onSelectionChange);
        document.addEventListener('copy', onCopyOrCut, true);
        document.addEventListener('cut', onCopyOrCut, true);

        return () => {
            document.removeEventListener('selectstart', onSelectStart, true);
            document.removeEventListener('selectionchange', onSelectionChange);
            document.removeEventListener('copy', onCopyOrCut, true);
            document.removeEventListener('cut', onCopyOrCut, true);
        };
    }, [html]);

    return (
        <div
            ref={rootRef}
            id={id}
            className={className}
            style={{ ...noSelectInlineStyle, ...style }}
            dangerouslySetInnerHTML={{ __html: html ?? '' }}
        />
    );
}
